import { Component, Input, OnInit } from "@angular/core";
import {
  ControlContainer,
  UntypedFormGroup,
  FormGroupDirective,
} from "@angular/forms";
import { forkJoin } from "rxjs";
import {
  RoleListItem,
  TargetUserType,
} from "../../models/user-management/role";
import { LookupService } from "../../services/lookup.service";

@Component({
    selector: "app-user-roles-selection",
    templateUrl: "./user-roles-selection.component.html",
    styleUrls: ["./user-roles-selection.component.scss"],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    standalone: false
})
export class UserRolesSelectionComponent implements OnInit {
  @Input() atLeastOneRequired: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() groupOfRoles: string = "";

  get formGroup(): UntypedFormGroup {
    return this.formGroupDirective.form;
  }

  allUsersChecked: boolean = false;

  lookups = {
    cdeRoles: [],
    auRoles: [],
  };

  constructor(
    private lookupService: LookupService,
    private readonly formGroupDirective: FormGroupDirective
  ) {}

  ngOnInit(): void {
    this.getLookups().subscribe(([cdeRoles, auRoles]) => {
      this.lookups.cdeRoles = cdeRoles.map((role: RoleListItem) => {
        return { id: role.name, label: role.description };
      });
      this.lookups.auRoles = auRoles.map((role: RoleListItem) => {
        return { id: role.name, label: role.description };
      });
    });

    if (this.atLeastOneRequired) {
      this.formGroup.setValidators(this.needAtLeastOne("cdeRoles", "auRoles"));
    }
  }

  getLookups() {
    if (this.groupOfRoles === "") {
      //no group is defined so show all the Roles
      return forkJoin([
        this.lookupService.getCdeRoles(),
        this.lookupService.getAuRoles(),
      ]);
    } else {
      //there is a group defined, so show the roles for that group
      return forkJoin([
        this.lookupService.getRolesByGroup(
          this.groupOfRoles,
          TargetUserType.StateEducationAgency
        ),
        this.lookupService.getRolesByGroup(
          this.groupOfRoles,
          TargetUserType.AdministrativeUnit
        ),
      ]);
    }
  }

  toggleAllUsersChecked(isChecked: boolean) {
    this.allUsersChecked = isChecked;
    if (isChecked) {
      const allCdeValues = this.lookups.cdeRoles.map((m) => m.id).join(", ");
      this.formGroup.get("cdeRoles").setValue(allCdeValues);
      const allAuValues = this.lookups.auRoles.map((m) => m.id).join(", ");
      this.formGroup.get("auRoles").setValue(allAuValues);
    } else {
      this.formGroup.get("cdeRoles").setValue("");
      this.formGroup.get("auRoles").setValue("");
    }
  }

  public checkboxChanged(
    formKey: string,
    checkboxValue: string,
    isChecked: boolean
  ) {
    const control = this.formGroup.get(formKey);
    const val = control.value || "";
    let selections = val.split(", ").filter((i) => i);
    if (isChecked) {
      selections.push(checkboxValue);
    } else {
      selections = selections.filter((i) => i !== checkboxValue);
    }
    control.setValue(selections.sort().join(", "));
  }

  public isCheckboxChecked(formKey: string, checkboxValue: string) {
    const val = this.formGroup.get(formKey).value || "";
    const selections = val.split(", ").filter((i) => i);
    return selections.includes(checkboxValue);
  }

  needAtLeastOne(listname1: string, listname2: string) {
    return function (frm: UntypedFormGroup) {
      let list1 = frm.get(listname1);
      let list2 = frm.get(listname2);

      if (list1.value == "" && list2.value == "") {
        return {
          atLeastOne: "Need at least one selection from CDE or AU list",
        };
      }
      return null;
    };
  }
}
